import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  ReportGroupKey,
  type ReportGroupAvailability,
  type ReportGroupReason,
  type SprintView,
  periodRange,
} from '@apm/contracts';
import { apiFetch } from '../../../lib/http';
import { reportApi, reportKeys } from '../api';
import { SummaryPanel } from './summary-panel';
import { AgingWipList } from './aging-wip-list';
import { BurndownChart } from './burndown-chart';
import { CrTrendChart } from './cr-trend-chart';
import { CycleTimeChart } from './cycle-time-chart';
import { DocumentStatusChart } from './document-status-chart';
import { EstimateAccuracyChart } from './estimate-accuracy-chart';
import { EvmChart } from './evm-chart';
import { FlowChart } from './flow-chart';
import { ObjectiveTrace } from './objective-trace';
import { PhaseGateList } from './phase-gate-list';
import { ResourceHistogram } from './resource-histogram';
import { RiskBurndownChart } from './risk-burndown-chart';
import { RiskMatrix } from './risk-matrix';
import { SlippageChart } from './slippage-chart';
import { ThroughputChart } from './throughput-chart';
import { PeriodPicker } from '../../tasks/components/period-picker';
import { PeriodComparison } from './period-comparison';
import { IssueReports } from '../../issues/components/issue-reports';
import { UnplannedChart } from './unplanned-chart';
import { pendingReason, showsGroup } from '../gating';
import { ReportPending } from './report-pending';
import { VelocityChart } from './velocity-chart';
import { Dialog } from '../../../components/ui/dialog';
import { LazyReport } from '../../../components/chart/lazy-report';
import { DelayRiskSection } from './delay-risk-section';
import { displayDateRange, todayPlus } from '../../../lib/date';

/**
 * Bốn nhóm chart — spec 16.6, đặt lại tên ở APM-REPORT-b.
 *
 * Danh sách lấy thẳng từ `@apm/contracts` chứ không viết lại ở đây: server
 * quyết định nhóm nào có dữ liệu (`GET .../reports/availability`), nên hai bên
 * lệch một tên là một nhóm không bao giờ bật lên được, mà chẳng bên nào báo lỗi.
 */
export const REPORT_GROUPS = ReportGroupKey.options;
export type ReportGroup = (typeof REPORT_GROUPS)[number];

/**
 * `waterfall` là **tên cũ** của nhóm `schedule`.
 *
 * Link báo cáo là thứ người ta dán vào chat và ghim vào tài liệu họp. Bỏ tên cũ
 * đi thì mọi link đã chia sẻ vẫn mở ra được, chỉ là mở **sai nhóm** và không ai
 * biết: `.catch()` nuốt giá trị lạ, người nhận thấy nhóm mặc định, và tưởng
 * người gửi nhầm.
 */
export const LEGACY_REPORT_GROUPS: Readonly<Record<string, ReportGroup>> = {
  waterfall: 'schedule',
};

/** Bốn tuần gần nhất — đủ để thấy xu hướng, đủ ngắn để mỗi cột còn đọc được. */
const WINDOW_DAYS = 28;

function isoDay(offsetDays: number): string {
  // Hôm nay của tổ chức, không phải ngày UTC — APM-TZ-i.
  return todayPlus(offsetDays);
}

interface ReportsPanelProps {
  readonly projectId: string;
  /**
   * ISO 8601. Có giá trị ⇒ mọi chart vẽ state tại thời điểm đó — luật #7.
   *
   * ⚠️ Không còn ô chọn ngày ở tab này (APM-REPORT-c), nhưng prop **ở lại**:
   * nó vẫn đi tới tận truy vấn của từng chart, và tab Tua lại dùng đúng đường
   * ấy. Bỏ prop đi là tự tay chặn Replay, không phải dọn mã thừa.
   */
  readonly asOf: string | undefined;
  readonly group: ReportGroup;
  /** Sprint đang xem, từ URL. `undefined` = cả dự án — APM-REPORT-c. */
  readonly sprintId: string | undefined;
  readonly onChange: (next: { group?: ReportGroup; sprintId?: string | undefined }) => void;
  /**
   * `task:update` ở dự án này — mở nút "Qua cổng pha" trong danh sách cổng pha.
   *
   * Cửa vào duy nhất của thao tác ấy, kể từ khi thanh chi tiết dưới đáy Gantt
   * bị bỏ (APM-GANTT-k).
   */
  readonly canEditTasks?: boolean;
  /**
   * Dự án bảo trì đang xem kỳ nào — APM-MAINT-e. `null` = không phải bảo trì.
   *
   * Kỳ **thay** cửa sổ cuộn 90 ngày mặc định: một dự án bảo trì báo cáo theo
   * tháng/quý cho khách hàng, và "90 ngày gần đây" không khớp với bất kỳ mốc
   * nào trong hợp đồng của họ.
   */
  readonly period?: string | null;
  /**
   * Đổi kỳ **ngay ở đây** — APM-MAINT-g.
   *
   * Trước đó kỳ chỉ đổi được ở tab Công việc, nên đọc báo cáo quý 3 rồi muốn
   * xem quý 2 là phải sang tab khác, đổi, rồi quay lại — ba cú bấm cho một thao
   * tác mà chính màn hình này sinh ra nhu cầu.
   *
   * Cùng tham số URL với tab Công việc, nên đổi ở đâu cũng như nhau và hai tab
   * không bao giờ nói về hai khoảng thời gian khác nhau.
   */
  readonly onPeriodChange?: (period: string) => void;
}

/**
 * Bộ báo cáo của **một** dự án — APM-801/802/803, nay là một tab của dự án.
 *
 * ## Vì sao nằm trong dự án, không phải một mục điều hướng riêng
 *
 * Mọi con số ở đây thuộc về đúng một dự án, và màn hình cũ mở ra bằng một bộ
 * chọn dự án **thứ hai** — người dùng chọn dự án ở tab Board rồi sang Báo cáo
 * lại chọn lần nữa, đôi khi chọn khác, và hai màn hình nói về hai dự án khác
 * nhau trong cùng một phiên làm việc. Là tab của dự án thì không còn chỗ cho
 * sự lệch đó.
 *
 * ## Vì sao chia nhóm thay vì xếp mười lăm chart lên một trang
 *
 * Không phải để trang gọn. Mười lăm chart nghĩa là mười lăm truy vấn
 * `event_log` chạy cùng lúc cho một lượt xem, và phần lớn trong đó người mở
 * không hỏi tới. Nhóm nào không hiện thì query của nó không chạy — React Query
 * chỉ gọi khi component được mount.
 *
 * Mốc thời gian và nhóm chart nằm ở **search param** của trang dự án
 * (docs/frontend-guide.md §2): một biểu đồ ở thời điểm quá khứ là thứ người ta
 * dán vào chat để hỏi nhau, và link phải mở ra đúng cái họ đang nhìn.
 */
export function ReportsPanel({
  projectId,
  asOf,
  group,
  sprintId,
  onChange,
  canEditTasks = false,
  period = null,
  onPeriodChange,
}: ReportsPanelProps): ReactNode {
  const { t } = useTranslation();
  /**
   * Sprint đang xem — APM-REPORT-c. `null` = cả dự án.
   *
   * Sống ở **URL** như tab Công việc, nên đổi tab không mất lựa chọn và link
   * dán cho nhau mở ra đúng thứ người gửi đang nhìn.
   *
   * Chọn sprint thì **khoảng ngày đi theo sprint**, không giữ cửa sổ 30 ngày
   * mặc định: burndown của một sprint chỉ có nghĩa trên đúng khoảng của nó.
   */
  const sprints = useQuery({
    queryKey: ['sprints', projectId],
    queryFn: () => apiFetch<SprintView[]>(`/projects/${projectId}/sprints`),
  });
  const picked = sprints.data?.find((sprint) => sprint.id === sprintId) ?? null;

  /**
   * Dự án có bao nhiêu cổng pha — APM-REPORT-e.
   *
   * Dùng chính endpoint của báo cáo cổng pha: nó đã trả về danh sách, và một
   * truy vấn thứ hai chỉ để đếm là hai nguồn sẽ lệch nhau. `staleTime` dài vì
   * số cổng pha chỉ đổi khi ai đó tạo hay xoá một cái — không phải chuyện xảy
   * ra trong một phiên đọc báo cáo.
   */
  const gates = useQuery({
    queryKey: reportKeys.phaseGates(projectId, ''),
    queryFn: () => reportApi.phaseGates(projectId),
    staleTime: 300_000,
  });
  const gateCount = gates.data?.gates.length ?? 0;
  /** `null` khi tham số URL trỏ tới một sprint không thuộc dự án đang xem. */
  const scoped = picked === null ? null : picked.id;

  /**
   * Nhóm nào có dữ liệu — APM-REPORT-b.
   *
   * Không phụ thuộc `asOf`: câu hỏi là "dự án này có loại dữ liệu ấy không",
   * và một nhóm biến mất giữa chừng vì người dùng kéo con trỏ thời gian thì
   * đọc thành lỗi. `staleTime` dài vì câu trả lời chỉ đổi khi dự án có thêm
   * baseline hoặc rủi ro đầu tiên — không phải chuyện xảy ra trong một phiên.
   */
  const availability = useQuery({
    queryKey: reportKeys.availability(projectId),
    queryFn: () => reportApi.availability(projectId),
    staleTime: 300_000,
  });

  /**
   * `undefined` = chưa biết ⇒ **coi như có**. Chặn trước khi biết thì mỗi lần
   * mở tab người dùng thấy một câu "nhóm này chưa có dữ liệu" nhoáng lên rồi
   * biến mất, và câu ấy đọc như một lỗi.
   */
  /**
   * Nhóm nào **hiện ra** — APM-REPORT-d, sửa ở APM-REPORT-g.
   *
   * ## Hai lý do vắng mặt, hai cách xử lý
   *
   * - **Không áp dụng cho loại dự án này** (`not_maintenance`, `no_end_date`):
   *   ẩn hẳn. Nhóm Bảo trì trên một dự án Agile sẽ **không bao giờ** có nội
   *   dung, nên một khung rỗng ở đó là một khung vĩnh viễn rỗng.
   * - **Điều kiện chưa đạt** (`no_baseline`, `no_tasks`, …): vẫn hiện, và nói
   *   ra cần gì. Người dùng bấm một nút là nó chạy — giấu nó đi là giấu mất
   *   chính cái nút ấy.
   *
   * Bản trước gộp hai chuyện này làm một và ẩn cả hai, nên "chưa chốt kế hoạch
   * gốc" đọc thành "sản phẩm không có báo cáo EVM".
   *
   * ⚠️ `undefined` = chưa biết ⇒ **coi như có**. Ẩn trước khi biết thì mỗi lần
   * mở tab cả trang nhấp nháy: năm mục hiện ra rồi hai mục biến mất, và mục
   * người dùng đang nhắm chuột vào trượt đi dưới tay họ.
   */
  const rowOf = (value: ReportGroup): ReportGroupAvailability | undefined =>
    availability.data?.groups.find((entry) => entry.group === value);

  const shows = (value: ReportGroup): boolean => showsGroup(rowOf(value));

  const needsBaseline = pendingReason(rowOf('schedule')) === 'no_baseline';

  const reasonOf = (value: ReportGroup): ReportGroupReason | null => rowOf(value)?.reason ?? null;

  /**
   * Phạm vi thời gian của mọi biểu đồ, theo thứ tự ưu tiên — APM-MAINT-e.
   *
   * 1. **Sprint** đang chọn, nếu có: câu hỏi của người mở một sprint là về
   *    sprint ấy.
   * 2. **Kỳ** của dự án bảo trì: nó không có sprint, và nó báo cáo theo
   *    tháng/quý cho khách hàng.
   * 3. Cửa sổ cuộn `WINDOW_DAYS` ngày — mặc định cũ, cho dự án chưa chọn gì.
   *
   * Sprint đứng trên kỳ dù trên thực tế hai thứ không bao giờ cùng có: dự án
   * bảo trì không chạy sprint. Viết thành thứ tự ưu tiên tường minh thay vì
   * dựa vào điều đó, để chỗ này không phải biết luật ấy mới đọc được.
   */
  const periodWindow = period ? periodRange(String(period)) : null;
  const range = picked
    ? { from: picked.startDate, to: picked.endDate, asOf }
    : periodWindow
      ? { from: periodWindow.from, to: periodWindow.to, asOf }
      : { from: isoDay(-(WINDOW_DAYS - 1)), to: isoDay(0), asOf };

  /** Hộp "Phân tích rủi ro tự động" — APM-PSET-n. */
  const [delayRiskOpen, setDelayRiskOpen] = useState(false);

  return (
    /*
      Một trang, năm segment, outline ở cột trái — APM-REPORT-d.

      ## Vì sao bỏ thanh tab

      Năm nhóm báo cáo trả lời năm câu hỏi **của cùng một cuộc họp**: dự án có
      ổn không, việc chảy ra sao, có kịp lịch không, quản trị thế nào, vận hành
      ra sao. Tab bắt người đọc nhớ mình đã xem gì và chưa xem gì, và bắt họ bấm
      năm lần để đọc hết — trong khi thứ họ cần là cuộn một mạch rồi quay lại
      chỗ nào cần.

      Outline giữ được cái mà tab cho: nhảy thẳng tới một phần. Nó bỏ đi cái mà
      tab lấy mất: nhìn thấy **có những gì**.

      ## Vì sao `sticky` chứ không cố định

      Cột trái dính theo khi cuộn, nhưng vẫn nằm trong luồng: màn hình hẹp thì
      nó xếp lên trên nội dung thay vì đè lên. Một outline đè lên biểu đồ ở
      1024px là một outline người ta muốn tắt.
    */
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      {/*
        Hộp "Phân tích rủi ro tự động" — APM-PSET-n.

        Khối này trước đây đứng **đầu cột phải**, trên cả bộ chọn sprint: mở tab
        Báo cáo ra là thấy một khung AI trước khi thấy bất kỳ con số nào, và nó
        đẩy toàn bộ báo cáo xuống. Nay nó là một nút ở đầu mục lục bên trái, nên
        cột phải dành trọn cho báo cáo (yêu cầu người dùng, 2026-09-18).
      */}
      {delayRiskOpen ? (
        <Dialog label={t('delayRisk.open')} size="lg" onClose={() => setDelayRiskOpen(false)}>
          <DelayRiskSection projectId={projectId} defaultOpen />
        </Dialog>
      ) : null}

      <ReportOutline
        shows={shows}
        reasonOf={reasonOf}
        /*
          Mở link `?chart=schedule` thì cuộn thẳng tới segment ấy — APM-REPORT-d.

          Tham số `chart` trước đây chọn tab; nay nó chọn **chỗ đứng**. Giữ lại
          thay vì bỏ: những link đã chia sẻ trong chat vẫn mở ra đúng phần người
          gửi đang nhìn, và đó là lý do tham số ấy nằm trên URL ngay từ đầu.
        */
        initial={group}
        onAnalyse={() => setDelayRiskOpen(true)}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {/*
        Chọn sprint **trước**, ở trên cùng — APM-REPORT-c, cùng chỗ với tab
        Công việc. Trước đây ô này nằm bên trong nhóm "Luồng công việc", nên mở
        tab Báo cáo ra là không thấy phần theo sprint ở đâu cả.

        ⚠️ Khác tab Công việc ở một điểm: "Cả dự án" là lựa chọn **hợp lệ** và
        là mặc định. Báo cáo cấp dự án có nghĩa thật (velocity nhiều sprint, cây
        mục tiêu, rủi ro còn mở), khác với board — nơi "mọi sprint" là một tập
        cột trộn lẫn không đọc được.
      */}
        {/*
        Bộ chọn kỳ đứng **trên cùng**, đúng chỗ bộ chọn sprint đứng ở dự án
        Agile — APM-MAINT-g. Thứ tự câu hỏi giống hệt tab Công việc: "kỳ nào"
        đứng trước "đọc biểu đồ gì".
      */}
        {period && onPeriodChange ? (
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker value={String(period)} onChange={onPeriodChange} />
          </div>
        ) : null}

        {(sprints.data ?? []).length > 0 ? (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              {t('reports.sprintFilter')}
              <select
                value={scoped ?? ''}
                onChange={(event) =>
                  onChange({ sprintId: event.target.value === '' ? undefined : event.target.value })
                }
                className="h-8 rounded-control border border-border-strong bg-background px-2 text-sm text-foreground"
              >
                <option value="">{t('reports.sprintFilterAll')}</option>
                {[...(sprints.data ?? [])]
                  .sort((a, b) => b.sequence - a.sequence)
                  .map((sprint) => (
                    <option key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </option>
                  ))}
              </select>
            </label>

            {picked ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                {displayDateRange(picked.startDate, picked.endDate)}
              </span>
            ) : null}
          </div>
        ) : null}

        {/*
        ⚠️ Nhóm không có dữ liệu vẫn **hiện nút**, chỉ mờ đi.

        Xoá hẳn nó khỏi thanh chọn là cách hỏng im lặng: người dùng nhớ "có một
        biểu đồ đường găng ở đâu đó", không thấy nữa, và đi tìm trong cấu hình —
        hoặc kết luận là mất tính năng. Bấm vào một nút mờ vẫn mở ra được, và
        thứ mở ra là **câu trả lời vì sao trống**, không phải bốn ô rỗng.
      */}
        {/*
        Mỗi chart tự tải dữ liệu của nó và tự hiện skeleton: dashboard nhiều
        chart mà chờ tuần tự thì cả trang trắng đến khi query cuối xong
        (docs/frontend-guide.md §5).
      */}
        {/*
        Nhóm "Sức khoẻ dự án" **là** tầng dự án — APM-REPORT-c.

        Mọi thứ trong đó (RAG, rủi ro còn mở, cây mục tiêu, việc đang ì) trả lời
        câu hỏi ở tầng dự án hoặc câu hỏi về **hiện tại**. Vẽ chúng dưới một cái
        nhãn ghi tên sprint là mời người đọc hiểu sai, nên chọn sprint thì nhóm
        này nói ra điều đó thay vì đổi số.
      */}
        {shows('summary') && <SegmentHeading group="summary" />}
        {shows('summary') && scoped === null && (
          <SummaryPanel projectId={projectId} range={range} asOf={asOf} />
        )}
        {shows('summary') && scoped !== null && <NotBySprint sprintName={picked?.name ?? ''} />}

        {shows('flow') && <SegmentHeading group="flow" />}
        {shows('flow') && (
          <>
            {/*
            Lọc theo sprint thì **ẩn** những biểu đồ không tách được theo sprint,
            và nói vì sao. Để nguyên chúng thì người dùng đọc một con số cấp dự
            án dưới một cái nhãn ghi tên sprint.
          */}
            {scoped === null ? (
              <LazyReport>
                <FlowChart projectId={projectId} range={range} />
              </LazyReport>
            ) : null}

            <div className="grid gap-4 @4xl:grid-cols-2">
              <LazyReport>
                <BurndownChart
                  projectId={projectId}
                  range={range}
                  {...(scoped === null ? {} : { sprintId: scoped })}
                />
              </LazyReport>
              <LazyReport>
                <ThroughputChart projectId={projectId} range={range} />
              </LazyReport>
              {scoped === null ? (
                <>
                  <LazyReport>
                    <CycleTimeChart projectId={projectId} range={range} />
                  </LazyReport>
                  <LazyReport>
                    <AgingWipList projectId={projectId} asOf={asOf} />
                  </LazyReport>
                </>
              ) : null}
              {/*
                Velocity chỉ có nghĩa khi dự án **chạy sprint** — APM-REPORT-e.

                Nó đo "bao nhiêu điểm mỗi sprint", nên ở dự án Waterfall nó vẽ
                ra một khung rỗng kèm câu "chưa có sprint nào" — một câu đúng
                nhưng vô nghĩa: Waterfall không có sprint, và sẽ không bao giờ
                có. Người đọc hiểu thành "thiếu dữ liệu, chắc phải đi tạo
                sprint", tức sản phẩm đang chỉ họ làm một việc không tồn tại.

                Hỏi **dữ liệu** chứ không hỏi `methodology`: một dự án Agile
                chưa tạo sprint nào cũng rơi vào đúng cảnh ấy, và một dự án đổi
                sang Waterfall vẫn giữ sprint cũ thì velocity của nó vẫn đọc
                được. Cùng luật với `decideGroups` (APM-REPORT-b).
              */}
              {(sprints.data ?? []).length > 0 ? (
                <LazyReport>
                  <VelocityChart projectId={projectId} />
                </LazyReport>
              ) : (
                <ReportPending
                  title={t('reports.velocityTitle')}
                  help="velocity"
                  reason={t('reports.pending.needSprint')}
                />
              )}
            </div>

            {scoped !== null ? <HiddenBySprint /> : null}
          </>
        )}

        {shows('schedule') && <SegmentHeading group="schedule" />}
        {shows('schedule') && (
          <>
            {/*
            EVM và trượt tiến độ đi theo **khoảng ngày**, nên chọn sprint là cắt
            chúng về đúng hai tuần ấy — đọc được. Cổng pha và biểu đồ nguồn lực
            thì không: cổng pha là mốc của cả dự án, còn biểu đồ nguồn lực nhìn
            về tương lai theo kế hoạch hiện tại (nó cũng không theo `asOf`).
          */}
            {/*
              Chưa chốt kế hoạch gốc thì EVM và trượt tiến độ **vẽ khung, không
              vẽ số** — APM-REPORT-g.

              Cả hai đo tiến độ *so với một mốc gốc*, nên không có mốc thì không
              có gì để tính. Nhưng đây là **điều kiện chưa đạt**, không phải
              "không áp dụng": một cú bấm ở tab Công việc là xong. Ẩn chúng đi
              là giấu mất chính cái nút ấy — người dùng kết luận sản phẩm không
              có EVM.
            */}
            {needsBaseline ? (
              <ReportPending
                title={t('reports.evmTitle')}
                help="evm"
                reason={t('reports.pending.needBaseline')}
              />
            ) : (
              <LazyReport>
                <EvmChart projectId={projectId} range={range} />
              </LazyReport>
            )}

            <div className="grid gap-4 @4xl:grid-cols-2">
              {needsBaseline ? (
                <ReportPending
                  title={t('reports.slippageTitle')}
                  help="slippage"
                  reason={t('reports.pending.needBaseline')}
                />
              ) : (
                <LazyReport>
                  <SlippageChart projectId={projectId} range={range} />
                </LazyReport>
              )}
              {scoped === null ? (
                <>
                  {/*
                    Cổng pha chỉ vẽ khi dự án **có** cổng pha — APM-REPORT-e.

                    Khác Velocity ở mức độ nhẹ hơn: khung rỗng của nó nói "dự án
                    chưa có cổng pha nào" — đúng và không chỉ sai đường. Nhưng ở
                    một dự án Agile thì câu ấy sẽ **luôn** đúng, nên nó là một
                    khung không bao giờ có nội dung.

                    Hỏi dữ liệu chứ không hỏi `methodology`: một dự án Agile
                    hoàn toàn có thể dựng cổng pha cho một đợt phát hành lớn, và
                    khi ấy báo cáo này là thứ họ cần.
                  */}
                  {gateCount > 0 ? (
                    <LazyReport>
                      <PhaseGateList projectId={projectId} asOf={asOf} canPass={canEditTasks} />
                    </LazyReport>
                  ) : (
                    <ReportPending
                      title={t('reports.gatesTitle')}
                      help="gates"
                      reason={t('reports.pending.needPhaseGate')}
                    />
                  )}
                  <LazyReport>
                    <ResourceHistogram projectId={projectId} />
                  </LazyReport>
                </>
              ) : null}
            </div>

            {scoped !== null ? <HiddenBySprint /> : null}
          </>
        )}

        {shows('maintenance') && <SegmentHeading group="maintenance" />}
        {shows('maintenance') && (
          <>
            {/*
            Bộ báo cáo của dự án bảo trì — APM-MAINT-d.

            Ba biểu đồ dòng chảy ở đây **không phải bản sao** của nhóm Luồng
            công việc: với dự án bảo trì thì chúng là bộ đọc chính, còn nhóm kia
            còn có burndown và velocity — hai thứ dựng trên sprint, mà dự án bảo
            trì không chạy sprint. Gom đúng thứ đọc được vào một chỗ rẻ hơn bắt
            người dùng nhớ biểu đồ nào trong nhóm kia còn có nghĩa.
          */}
            {/*
            Bảng so kỳ đứng **đầu** nhóm — APM-MAINT-f.

            Nó là câu trả lời ngắn nhất cho câu hỏi duy nhất mà người vận hành
            mở tab này để hỏi ("có kham nổi không"), còn ba biểu đồ dòng chảy
            bên dưới là phần giải thích vì sao. Đặt nó sau chúng thì người đọc
            phải xem hết phần giải thích mới tới câu trả lời.

            Chỉ hiện khi có kỳ: nhóm báo cáo này chỉ bật ở dự án bảo trì, nhưng
            `period` vẫn có thể vắng nếu ai đó mở bằng một đường dẫn cũ.
          */}
            {period ? (
              <LazyReport>
                <PeriodComparison projectId={projectId} period={String(period)} />
              </LazyReport>
            ) : null}

            <LazyReport>
              <UnplannedChart projectId={projectId} range={range} />
            </LazyReport>

            <div className="grid gap-4 @4xl:grid-cols-2">
              <LazyReport>
                <ThroughputChart projectId={projectId} range={range} />
              </LazyReport>
              <LazyReport>
                <CycleTimeChart projectId={projectId} range={range} />
              </LazyReport>
              <LazyReport>
                <AgingWipList projectId={projectId} asOf={asOf} />
              </LazyReport>
            </div>
          </>
        )}

        {/*
          Vấn đề vận hành — nhóm riêng, ngay dưới "Vận hành & bảo trì"
          (APM-PSET-m, yêu cầu người dùng 2026-09-18).

          Trước đây năm biểu đồ này nằm **trong** nhóm bảo trì. Hai chỗ sai vì
          thế: một dự án Agile nhận sự cố từ người dùng thì không có chỗ nào đọc
          chúng, còn một dự án bảo trì chưa nhận sự cố nào lại mở ra năm biểu đồ
          trống. Tách ra thì mỗi nhóm có đúng một ngưỡng mở của nó.

          Đặt ngay dưới nhóm bảo trì vì đó là chỗ người vận hành đang đọc: hai
          nhóm trả lời hai nửa của cùng một câu — "đội có kham nổi lượng việc
          không" và "người dùng đang gặp gì, mình trả lời nhanh tới đâu".

          ⚠️ Truyền `range` **không kèm `asOf`**: `from`/`to` là trục thời gian
          của chính nhóm (luật #7 ngoại lệ 2).
        */}
        {shows('issues') && <SegmentHeading group="issues" />}
        {shows('issues') && (
          /*
            Bọc **cả khối**, không bọc từng biểu đồ: năm biểu đồ trong đây dùng
            chung đúng một `useQuery`. Bọc lẻ thì năm khung giữ chỗ cùng chờ một
            lượt gọi, và cái nào vào tầm nhìn trước cũng kéo theo cả bốn cái kia.
          */
          <LazyReport>
            <IssueReports projectId={projectId} range={{ from: range.from, to: range.to }} />
          </LazyReport>
        )}

        {shows('governance') && <SegmentHeading group="governance" />}
        {shows('governance') && (
          <>
            {/*
            Độ chính xác ước lượng và xu hướng CR đi theo khoảng ngày nên cắt
            được. Bốn thứ còn lại thuộc **dự án**: rủi ro không thuộc sprint nào,
            mục tiêu sống qua nhiều sprint, và trạng thái tài liệu là câu hỏi về
            hiện tại.
          */}
            <div className="grid gap-4 @4xl:grid-cols-2">
              {scoped === null ? (
                <>
                  <LazyReport>
                    <RiskMatrix projectId={projectId} asOf={asOf} />
                  </LazyReport>
                  <LazyReport>
                    <RiskBurndownChart projectId={projectId} range={range} />
                  </LazyReport>
                </>
              ) : null}
              <LazyReport>
                <EstimateAccuracyChart projectId={projectId} range={range} />
              </LazyReport>
              {scoped === null ? (
                <LazyReport>
                  <ObjectiveTrace projectId={projectId} asOf={asOf} />
                </LazyReport>
              ) : null}
              <LazyReport>
                <CrTrendChart projectId={projectId} range={range} />
              </LazyReport>
              {scoped === null ? (
                <LazyReport>
                  <DocumentStatusChart projectId={projectId} asOf={asOf} />
                </LazyReport>
              ) : null}
            </div>

            {scoped !== null ? <HiddenBySprint /> : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Đầu mỗi segment: một đường kẻ và tên nhóm — APM-REPORT-d.
 *
 * ## Vì sao chữ **to hơn nhưng nhạt hơn**
 *
 * Trên một trang cuộn dài, tiêu đề segment là **biển chỉ đường**, không phải
 * nội dung. Cỡ chữ nói "đây là một mốc lớn"; màu nhạt nói "đừng đọc tôi, đọc
 * mấy con số bên dưới". Để nó đậm bằng tiêu đề biểu đồ thì mắt dừng lại ở đúng
 * chỗ không có thông tin nào.
 *
 * Đường kẻ làm phần còn lại: nó cắt trang thành từng khối đọc được, và nó là
 * thứ người ta thấy trước cả khi đọc chữ.
 *
 * `scroll-mt-16` chừa chỗ cho thanh điều hướng dính ở đỉnh: thiếu nó thì bấm
 * outline sẽ cuộn tiêu đề **lên dưới** thanh ấy, và người dùng tưởng mình nhảy
 * nhầm chỗ.
 */
function SegmentHeading({ group }: { readonly group: ReportGroup }): ReactNode {
  const { t } = useTranslation();

  return (
    <h3
      id={segmentId(group)}
      className="scroll-mt-16 border-t border-border pt-4 text-lg font-medium text-muted-foreground/70"
    >
      {t(`reports.tab${group[0]!.toUpperCase()}${group.slice(1)}`)}
    </h3>
  );
}

/** `id` của segment — một chỗ dựng, để outline và neo không bao giờ lệch nhau. */
function segmentId(group: ReportGroup): string {
  return `report-${group}`;
}

/**
 * Outline năm dòng ở cột trái — APM-REPORT-d.
 *
 * ## Vì sao nhóm vắng vẫn có một dòng
 *
 * Thanh tab cũ giữ nhóm rỗng ở dạng mờ vì một lý do vẫn đúng: "xoá hẳn khỏi
 * thanh chọn là cách hỏng im lặng — người dùng nhớ có một biểu đồ ở đâu đó,
 * không thấy nữa, rồi kết luận là mất tính năng."
 *
 * Nhưng nó **không** được là một link: một mục outline trỏ tới segment không
 * tồn tại là một cú bấm không đi đâu cả. Nên dòng ấy gạch ngang, không bấm
 * được, và mang lý do trong tooltip — cùng câu chữ mà khối "nhóm này chưa có
 * dữ liệu" từng nói, chỉ là nói ngay cạnh tên nhóm thay vì chiếm cả trang.
 */
function ReportOutline({
  shows,
  reasonOf,
  initial,
  onAnalyse,
}: {
  readonly shows: (group: ReportGroup) => boolean;
  readonly reasonOf: (group: ReportGroup) => ReportGroupReason | null;
  readonly initial: ReportGroup;
  /** Mở hộp "Phân tích rủi ro tự động" — APM-PSET-n. */
  readonly onAnalyse: () => void;
}): ReactNode {
  const { t } = useTranslation();

  /*
    Cuộn tới segment của link chia sẻ, **một lần**.

    Phụ thuộc rỗng chứ không theo `initial`: người dùng cuộn tay xuống rồi một
    lượt vẽ lại kéo họ về đầu là thứ không cách nào đoán trước — và nó chỉ xảy
    ra với người có mạng chậm, tức khó tái hiện nhất.

    `requestAnimationFrame`: segment chỉ tồn tại sau lượt vẽ đầu, nên gọi ngay
    trong effect thì `getElementById` trả `null` và cú cuộn im lặng không xảy ra.
  */
  useEffect(() => {
    if (initial === 'summary') return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(segmentId(initial))?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [initial]);

  return (
    <nav
      aria-label={t('nav.reports')}
      className="flex shrink-0 flex-row flex-wrap gap-1 lg:sticky lg:top-4 lg:w-48 lg:flex-col"
    >
      {/*
        Nút phân tích đứng **trên** cả mục lục — APM-PSET-n.

        Nó không phải một mục lục thứ sáu: mục lục nhảy tới một chỗ trên trang,
        còn nút này mở một hộp. Nên nó là một viên kính (`btn-glass`) có icon để
        đọc ra là một hành động, nhưng giữ cỡ chữ của mục lục — cột trái là một
        khối, và một nút to hơn ở đầu sẽ đọc thành tiêu đề của khối ấy.

        Khoảng hở dưới (`mb-2`) tách nó khỏi danh sách — trước đây là một đường
        kẻ, nhưng viên kính tự đã là một vật riêng; hai thứ khác loại đứng liền
        nhau thì mắt gộp chúng làm một.
      */}
      <button
        type="button"
        data-tour="reports-delay-risk"
        onClick={onAnalyse}
        // Bo 16px chứ không tròn hết cỡ: cột chỉ 192px, nhãn xuống hai dòng, và một
        // viên thuốc hai dòng chữ là một viên nang méo.
        className="btn-glass mb-2 flex items-center gap-1.5 rounded-2xl px-3 py-1.5 text-left text-sm text-muted-foreground hover:text-foreground"
      >
        <Sparkles aria-hidden className="size-3.5 shrink-0" />
        {t('delayRisk.open')}
      </button>

      {REPORT_GROUPS.map((group) => {
        const label = t(`reports.tab${group[0]!.toUpperCase()}${group.slice(1)}`);
        const reason = reasonOf(group);

        if (!shows(group)) {
          return (
            <span
              key={group}
              className="cursor-help rounded-DEFAULT px-2 py-1 text-sm text-muted-foreground/50 line-through"
              title={reason ? t(`reports.groupEmptyReason.${reason}`) : undefined}
            >
              {label}
            </span>
          );
        }

        return (
          <a
            key={group}
            href={`#${segmentId(group)}`}
            className="rounded-DEFAULT px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {label}
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Nhóm này không đổi theo sprint — APM-REPORT-c.
 *
 * Nói ra thay vì vẽ số cấp dự án dưới một cái nhãn ghi tên sprint. Ẩn im lặng
 * thì người dùng tưởng nhóm hỏng; vẽ nguyên thì họ tưởng đó là số của sprint.
 */
function NotBySprint({ sprintName }: { readonly sprintName: string }): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="rounded-DEFAULT border border-dashed border-border p-6 text-center">
      <p className="text-sm font-medium">{t('reports.notBySprint', { name: sprintName })}</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
        {t('reports.notBySprintHelp')}
      </p>
    </div>
  );
}

/**
 * Một dòng nói **có thứ đang bị ẩn** vì đang lọc theo sprint.
 *
 * Bốn biểu đồ không tách được theo sprint vì ý nghĩa, không phải vì thiếu code
 * (bảng ở docs/03-backlog.md §Báo cáo). Ẩn im lặng thì người dùng tưởng biểu đồ
 * hỏng và đi báo lỗi.
 */
function HiddenBySprint(): ReactNode {
  const { t } = useTranslation();

  return <p className="text-xs text-muted-foreground">{t('reports.sprintFilterHidden')}</p>;
}
